<script setup lang="ts">
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import '@splidejs/vue-splide/css'

const props = withDefaults(
	defineProps<{
		title: string
		text: string
		image: string
		testimonial: {
			image: string
			text: string
			information: string
		}
		slides: string[]
		background?: string
		slideBackground?: string
	}>(),
	{
		background: 'bg-white',
		slideBackground: 'bg-gray-200',
	},
)

const sliderElement = ref(null)

const options = {
	type: 'fade',
	perPage: 1,
	perMove: 1,
	gap: '2rem',
	arrows: false,
	pagination: false,
}

const mediaQuery = ref(null)
const device = ref('desktop')

onMounted(() => {
	mediaQuery.value = window.matchMedia('(max-width: 768px)')
	device.value = mediaQuery.value.matches ? 'mobile' : 'desktop'

	mediaQuery.value.addEventListener('change', (e) => {
		device.value = e.matches ? 'mobile' : 'desktop'
	})
})

onUnmounted(() => {
	if (mediaQuery.value) {
		mediaQuery.value.removeEventListener('change', (e) => {
			device.value = e.matches ? 'mobile' : 'desktop'
		})
	}
})

const currentIndex = ref(0)

const handleMove = splide => {
	currentIndex.value = splide.index
}

const showPrevButton = computed(() => {
	return currentIndex.value > 0
})

const showNextButton = computed(() => {
	return currentIndex.value < props.slides.length - 1
})
</script>

<template>
	<div :class="background">
		<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
			<div class="grid md:grid-cols-2 gap-12">
				<div>
					<h2 class="text-3xl text-highlight mt-2 text-balance" v-html="title"></h2>

					<div class="w-24 h-1 bg-[linear-gradient(90deg,#17408a_0%,#17408a_50%,#bc8966_50%,#bc8966_100%)] my-4"></div>

					<p class="text-color !text-base md:!text-lg pt-0 mt-8" v-html="text"></p>
				</div>

				<img
					:src="image"
					class="w-full h-auto"
				/>
			</div>

			<div class="grid md:grid-cols-2 gap-12 mt-8">
				<div class="flex items-center gap-4 p-6 bg-[#173f89] rounded-2xl text-white">
					<img
						:src="testimonial.image"
						class="w-24 h-24"
					/>
					<div>
						<p class="!text-base md:!text-lg text-inherit text-start my-0 text-pretty mb-2" v-html="`&quot;${testimonial.text}&quot;`"></p>
						<p class="!text-base md:!text-lg text-inherit !text-base pt-0 font-bold mb-0" v-html="testimonial.information"></p>
					</div>
				</div>

				<div class="flex flex-col gap-4">
					<Splide
						ref="sliderElement"
						:options="options"
						class="max-w-full"
						@splide:moved="handleMove"
					>
						<SplideSlide
							v-for="(slide, index) in slides"
							:key="index"
							@move="handleMove"
							:class="`h-fit p-6 ${slideBackground} rounded-2xl !text-base md:!text-lg text-highlight w-full`"
						>
							{{ slide }}
						</SplideSlide>
					</Splide>

					<div class="flex gap-4">
						<button
							:disabled="!showPrevButton"
							class="disabled:opacity-30 hover:-translate-x-0.5 transition-transform"
							@click="() => sliderElement.go('-1')">
							<Icon name="ph:caret-left-bold" class="text-2xl" />
						</button>

						<div class="flex justify-center items-center w-full gap-2">
							<template
								v-for="(_, index) in props.slides"
								:key="index"
							>
								<button
									:disabled="currentIndex === index"
									class="w-3 h-3 rounded-full bg-gray-300 disabled:bg-black"
									@click="() => sliderElement.go(index)"></button>
							</template>
						</div>

						<button
							:disabled="!showNextButton"
							class="disabled:opacity-30 hover:-translate-x-0.5 transition-transform"
							@click="() => sliderElement.go('+1')">
							<Icon name="ph:caret-right-bold" class="text-2xl" />
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
